<script setup lang="ts">
import { toHead } from 'vue-datocms'
import { queryArticle } from '~/graphql/article'
import {
  useSchemaGraph,
  buildSchemaWebPage,
  buildSchemaArticle,
  buildSchemaImage
} from '~/composables/schema'
import { schemaIds } from '~/types/schema'
import { ModelApiKey } from '~/types/models'

const appConfig = useAppConfig()
const route = useRoute()
const { throwError } = useNuxtError()

const { data, error } = await useDatoQuery(queryArticle, {
  variables: {
    slug: route.params.slug as string
  }
})

if (!data.value?.article || error.value) {
  console.error(error.value)
  throwError(404)
}

if (import.meta.env.SSR) {
  useTheme(AppTheme.DARK)
}

onMounted(() => {
  if (import.meta.client) {
    useTheme(AppTheme.DARK)
  }
})

const { formatLong } =
  useDate(data.value?.article?.createdAt ? new Date(data.value?.article?.createdAt) : undefined) ??
  {}

const { data: pager } = await useCollectionPager(ModelApiKey.Article, route.params.slug as string)

useHead(() => {
  return toHead(data.value?.article?._seoMetaTags || [])
})

const config = useRuntimeConfig()
const { usePageSchema } = useSchemaGraph()
const ids = schemaIds(config.public.appUrl)
const base = config.public.appUrl.replace(/\/$/, '')
const webpageId = `${base}${route.path}#webpage`
const imageId = `${base}${route.path}#image`
const article = data.value?.article
const articleImage = article?.image

usePageSchema([
  ...(articleImage
    ? [
        buildSchemaImage({
          id: imageId,
          url: articleImage.url,
          width: articleImage.width ?? undefined,
          height: articleImage.height ?? undefined,
          caption: articleImage.alt ?? undefined
        })
      ]
    : []),
  buildSchemaArticle({
    headline: article?.title ?? '',
    description: article?.excerpt ?? undefined,
    imageId: articleImage ? imageId : undefined,
    datePublished: article?.createdAt ?? undefined,
    isPartOf: { '@id': webpageId },
    mainEntityOfPage: { '@id': webpageId },
    publisher: { '@id': ids.ORG }
  }),
  buildSchemaWebPage({
    id: webpageId,
    url: `${base}${route.path}`,
    name: article?.title ?? undefined,
    inLanguage: 'en',
    isPartOf: { '@id': ids.WEBSITE },
    primaryImageOfPage: articleImage ? { '@id': imageId } : undefined
  })
])
</script>

<template>
  <div v-if="data?.article">
    <ClientOnly v-if="appConfig.ui.enableFluidThree">
      <FluidBlack />
    </ClientOnly>
    <article class="relative pt-header-h lg:pt-header-h-lg">
      <div class="container">
        <Image
          v-if="data.article.image"
          class="aspect-4/3 w-full object-cover md:aspect-cinema"
          preload
          :src="
            data.article.image.responsiveImage
              ? data.article.image.responsiveImage?.src
              : data.article.image.url
          "
          :srcset="
            data.article.image.responsiveImage
              ? data.article.image.responsiveImage?.webpSrcSet
              : undefined
          "
          :width="
            data.article.image.responsiveImage
              ? data.article.image.responsiveImage?.width
              : (data.article.image.width ?? 1280)
          "
          :height="
            data.article.image.responsiveImage
              ? data.article.image.responsiveImage?.height
              : (data.article.image.height ?? 853)
          "
          :alt="data?.article!.image?.alt!"
          sizes="calc(98.22vw - 27px)"
        />
      </div>

      <header class="mx-auto mt-8 max-w-editor px-4 lg:mt-15 lg:px-10">
        <Heading v-if="data.article.title" :size="4">{{ data.article.title }}</Heading>
        <div
          class="mx-auto mb-8 mt-4 flex w-full flex-col justify-between text-sm sm:flex-row lg:mb-12 lg:mt-2.5"
        >
          <div class="flex gap-2">
            <nav
              v-if="data.article.category.length"
              aria-label="Category"
              class="flex gap-2 uppercase"
            >
              <TagLink
                v-for="(category, idx) in data.article.category"
                :key="category.id"
                :data="category"
                :show-comma="idx < (data.article.category?.length ?? 0) - 1"
              />
            </nav>
            <span v-if="data.article.category.length">/</span>
            <div>
              <time :datetime="data.article.createdAt"> {{ formatLong }}</time>
            </div>
          </div>
          <div v-if="data.article.readTime">
            {{ data.article.readTime }}
          </div>
        </div>
      </header>
      <div v-if="data.article.leadText" class="text-xl lg:text-2xl">
        {{ data.article.leadText }}
      </div>
      <div class="mx-auto max-w-editor px-4 lg:px-10">
        <hr class="my-8 lg:my-12" />
      </div>
      <DatoStructuredText :data="data.article.body" class="editor-full" />
      <div class="mx-auto max-w-editor px-4 lg:px-10">
        <CollectionPager class="mt-12 lg:mt-20" :prev="pager.prev" :next="pager.next" />
      </div>
    </article>
    <aside class="container mb-20 lg:mb-24">
      <SectionNewsletterSubscription
        v-if="data.articleCommonSection?.sections.length"
        class="my-12"
        :data="data.articleCommonSection?.sections[0]"
        :module-index="0"
      />
      <Heading :level="3" :size="5" class="mb-8 lg:mb-15"><TextReveal>Up next</TextReveal></Heading>
      <ArticleFilteredList
        :type="CardType.NEWS_LATEST"
        :filter-by-article-slugs="[data.article.slug]"
        :records-count="2"
      />
      <div class="mx-auto mt-8 w-fit">
        <Button variant="outline" label="View all" to="/news" />
      </div>
    </aside>
  </div>
</template>
